import axios from "axios";
import { clearSession } from "../auth/session";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL?.trim() || "/api";
const apiTimeoutMs = Number(process.env.REACT_APP_API_TIMEOUT_MS || 120000);

const getSafeTimeout = (value) =>
    Number.isFinite(value) && value > 0 ? value : 120000;

const isTechnicalNetworkMessage = (value) => {
    const text = String(value || "").trim().toLowerCase();
    return Boolean(
        text &&
        (
            text.includes("econnreset") ||
            text.includes("econnrefused") ||
            text.includes("etimedout") ||
            text.includes("enotfound") ||
            text.includes("eai_again") ||
            text.includes("socket hang up") ||
            text.includes("network error") ||
            text.includes("timeout of") ||
            text.includes("request aborted")
        )
    );
};

const normalizeTechnicalErrorMessage = (message, status = 0) => {
    if (isTechnicalNetworkMessage(message)) {
        if (String(message || "").toLowerCase().includes("timeout")) {
            return "Request timed out. Please check your connection and try again.";
        }

        return "Connection was interrupted. Please check your internet and try again.";
    }

    if (status >= 500 && !message) {
        return "Server error. Please try again.";
    }

    return message;
};

const buildRequestErrorMessage = (error) => {
    if (error.code === "ECONNABORTED") {
        return "Request timed out. Please check your connection and try again.";
    }

    if (!error.response) {
        return "Network connection lost. Please check your internet and try again.";
    }

    const { status, data } = error.response;
    if (data && typeof data === "object" && data.message) {
        return normalizeTechnicalErrorMessage(data.message, status);
    }

    if (typeof data === "string" && data.trim()) {
        return normalizeTechnicalErrorMessage(data.trim(), status);
    }

    if (status >= 500) {
        return "Server error. Please try again.";
    }

    if (status >= 400) {
        return "Request failed. Please check the form and try again.";
    }

    return "Request failed. Please try again.";
};

const instance = axios.create({
    baseURL: apiBaseUrl,
    timeout: getSafeTimeout(apiTimeoutMs),
});

// Attach token automatically
instance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("token");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Handle global errors
instance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            clearSession();

            if (window.location.pathname !== "/") {
                window.location.replace("/");
            }
        }

        const message = buildRequestErrorMessage(error);
        error.userMessage = message;

        if (!error.response) {
            error.response = {
                status: 0,
                data: { message },
            };
        } else if (!error.response.data || typeof error.response.data !== "object") {
            error.response.data = { message };
        } else if (!error.response.data.message) {
            error.response.data.message = message;
        }

        return Promise.reject(error);
    }
);

export default instance;
